import React from 'react';
import PropTypes from 'prop-types';

import { Helpers } from '../../../core/src/helpers';
import Price from '../../../shared/components/price';

import './product-price.less';

/**
 * Product price component displays price and promotions for the given product
 */
class ProductPrice extends React.Component {
    static propTypes = {
        /**The product object { sku, itemId, prodId, prdClass }] */
        product: PropTypes.object.isRequired,
        /**Price and inventory information*/
        price: PropTypes.object,
        /**Callback function for fetching price info if price object is missing*/
        fetchPrices: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.getMinimumVariantPrice = this.getMinimumVariantPrice.bind(this);
        const { product } = props || {};
        //flag product price for prefetch on
        if (typeof window === 'undefined' && product && product.itemId && product.prdClass !== 'EXT') {
            const isParentPrd = product.prdClass === 'PRT' && product.sku === product.itemId;
            this.props.viewPrice(isParentPrd ? product.prodId : product.itemId);
        }
    }

    componentDidMount() {
        this.fetchProductPrice();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.product.itemId !== this.props.product.itemId) {
            this.fetchProductPrice();
        }
    }

    fetchProductPrice = () => {
        const { price, product } = this.props;
        const isParentPrd = product.prdClass === 'PRT' && product.sku === product.itemId;
        if (price.regularPrice === '' && product.itemId && product.prdClass !== 'EXT') {
            //TODO: Figure out a better way to get parent product pricing
            this.props.fetchPrices(isParentPrd ? [product.prodId] : [product.itemId]);
        }
    };

    getMinimumVariantPrice(items) {
        return Object.keys(items).reduce((minPrice, key) => {
            let item = items[key];
            let price = item.salePrice || item.regularPrice;
            if (price < minPrice) {
                return price;
            } else {
                return minPrice;
            }
        }, Number.MAX_SAFE_INTEGER);
    }

    render() {
        let {
            product,
            price,
            priceLabels,
            user,
            deal,
            variantId,
            pricePrefixText = '',
            className,
            totalPrice = null,
            showPreOrderMsg,
        } = this.props;
        const hasVariantPricing = Helpers.isGiftCard(product) && price.items && Object.keys(price.items).length;
        const spClassName = 'sale-price ' + (deal ? 'intraday-deal' : '');
        const { quantity = 1 } = product;
        const { invAvaDate, preOrder } = price;
        const { poboShipMsg = 'Estimated shipping date:' } = priceLabels || {};
        let onsale = deal || (price.salePrice && price.regularPrice > price.salePrice);
        let salePrice = (deal && deal.dealPrice) || price.salePrice || price.regularPrice;
        let defaultPrefixText = product.prdClass === 'CTO' || (hasVariantPricing && !variantId) ? 'Price starting at ' : null;
        pricePrefixText = pricePrefixText || defaultPrefixText;

        if (hasVariantPricing) {
            const itemPrice = price.items[variantId];
            salePrice = itemPrice
                ? itemPrice.salePrice || itemPrice.regularPrice
                : this.getMinimumVariantPrice(price.items);

            onsale = deal || (itemPrice && itemPrice.salePrice && itemPrice.regularPrice > itemPrice.salePrice);
        }

        //don't show price for 'supplies' type products
        if (price.productType === 'supplies') {
            return <div className="prd-price"></div>;
        }

        price = Object.assign({}, price, { salePrice });

        //total price should override all mainly because of bundling
        if (totalPrice) {
            price = Object.assign({}, price, totalPrice);
        }

        const priceProps = {
            className: '',
            showListPrice: price.regularPrice > price.salePrice || onsale || price.inCartPrice,
            showSalePrice: price.salePrice || hasVariantPricing,
            price,
            spClassName,
            pricePrefixText,
        };

        const regularPriceLabel = Helpers.formatCurrency(price.regularPrice, undefined, true);
        const salePriceLabel = Helpers.formatCurrency(price.salePrice, undefined, true);
        const priceAriaLabel =
            `Sale price starting at ${salePriceLabel}.` +
            (priceProps.showListPrice ? ` Regular price starting at ${regularPriceLabel}` : '');

        // to be able to test all that branching at the beginning of this method
        // TODO: untagle that branching into separate functions for easy testing and maintennance
        this.priceProps = priceProps;

        return (
            <div
                className={
                    'prd-price ' +
                    (price.inCartPrice ? 'incart-promo ' : '') +
                    (price.isccf ? 'ccf' : '') +
                    (Helpers.isProductOffline(this.props.price) ? 'offline-product' : '') +
                    (className ? className : '')
                }
                aria-label={priceAriaLabel}
                tabIndex={0}
            >
                <div className="tier-msg">
                    {user && user.TierPriceMsg && price.tierPrice ? user.TierPriceMsg : null}
                </div>
                {
                    //saftey check, never show a price of $0
                    salePrice > 0 ? <Price {...priceProps} /> : null
                }
                {price.inCartPrice ? <div className="price-promo">See price in cart</div> : null}
                {preOrder && showPreOrderMsg && (
                    <div className="preorder-message">{`${poboShipMsg} ${invAvaDate}`}</div>
                )}
                {price.isccf ? (
                    <div className="call-center">
                        <span className="gm-points">GM: {Helpers.formatCurrency(price.gmPoints)}</span>
                        <span className="inv-count">Stock: {price.stock}</span>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default ProductPrice;
